import React from 'react';
import { Button, Switch, Textarea } from '@heroui/react';
import { RiSendPlaneLine, RiStickyNoteLine } from '@remixicon/react';

interface ContactMessageComposerProps {
  onSubmit: (payload: { body: string; isNote: boolean }) => Promise<void> | void;
  isSubmitting?: boolean;
  defaultIsNote?: boolean;
}

export function ContactMessageComposer({ onSubmit, isSubmitting = false, defaultIsNote = false }: ContactMessageComposerProps) {
  const [body, setBody] = React.useState('');
  const [isNote, setIsNote] = React.useState(defaultIsNote);
  const [error, setError] = React.useState<string | null>(null);
  const [isSending, setIsSending] = React.useState(false);

  const reset = React.useCallback(() => {
    setBody('');
    setIsNote(defaultIsNote);
    setError(null);
  }, [defaultIsNote]);

  const handleSubmit = React.useCallback(async () => {
    if (!body.trim()) {
      setError('Message body is required');
      return;
    }

    setError(null);

    try {
      setIsSending(true);
      await onSubmit({ body: body.trim(), isNote });
      reset();
    } finally {
      setIsSending(false);
    }
  }, [body, isNote, onSubmit, reset]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className="rounded-large border border-default-200 bg-content1 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Reply / Internal Note</h3>
        <Switch
          className="min-w-[180px]"
          startContent={<RiStickyNoteLine className="text-warning h-4 w-4" />}
          isSelected={isNote}
          onValueChange={setIsNote}
          size="sm"
          color="warning"
        >
          Internal Note
        </Switch>
      </div>

      <div className="mt-3 space-y-3" onKeyDown={handleKeyDown}>
        <Textarea
          minRows={4}
          placeholder={isNote ? 'Add an internal note (not sent to customer)...' : 'Write your reply...'}
          value={body}
          onValueChange={setBody}
          isInvalid={Boolean(error)}
          errorMessage={error ?? undefined}
        />

        <div className="flex items-center justify-between text-sm text-default-500">
          <span>Press ⌘ + Enter (or Ctrl + Enter) to send quickly.</span>
          <Button
            color={isNote ? 'warning' : 'primary'}
            endContent={<RiSendPlaneLine className="h-4 w-4" />}
            isLoading={isSubmitting || isSending}
            onPress={handleSubmit}
          >
            {isNote ? 'Save Note' : 'Send Reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}

